import type { AssetRef, OwnerCommand, PhotoPick, SiteSeo } from "../api/contract";

/** Search engines show about this much of a title and a description before cutting it off. */
export const TITLE_SHOWN = 60;
export const DESCRIPTION_SHOWN = 160;
/** What the server accepts. */
export const MAX_TITLE = 70;
export const MAX_DESCRIPTION = 300;

export interface PageDetailsForm { title: string; description: string }

export interface SiteDetailsForm {
  title: string;
  description: string;
  /** A new upload, `null` to go back to the generated icon, or undefined to leave it. */
  icon?: AssetRef | PhotoPick | null;
  /** A new share image, `null` to go back to each page's own picture, or undefined to leave it. */
  image?: AssetRef | PhotoPick | null;
  /** The Google tag ID as typed; undefined leaves it alone. */
  analytics?: string;
  /** The site's language tag; empty means the design's own. Undefined leaves it alone. */
  language?: string;
  /** The site's IANA time zone; empty means the UTC day. Undefined leaves it alone. */
  timeZone?: string;
  /** Per-page overrides by path; empty text means automatic. */
  pages?: Record<string, PageDetailsForm>;
}

type SavedSeo = Pick<SiteSeo, "title" | "description" | "icon"> & Partial<Pick<SiteSeo, "image" | "analytics" | "language" | "timeZone" | "pages">>;
type SiteSet = Extract<OwnerCommand, { kind: "site.set" }>;

/** The tag IDs Google gives out: a measurement ID (G-), a Google tag (GT-) or an Ads ID (AW-). */
export const GOOGLE_TAG_PATTERN = /^(G|GT|AW)-[A-Z0-9]{4,20}$/;

/** What was typed, as a tag ID: spaces dropped and letters upper-cased. */
export const tagId = (text: string) => text.replace(/\s+/g, "").toUpperCase();

export function siteDetailsForm(seo: Pick<SiteSeo, "title" | "description"> & Partial<Pick<SiteSeo, "analytics" | "language" | "timeZone" | "pages">>): SiteDetailsForm {
  return {
    title: seo.title ?? "",
    description: seo.description ?? "",
    analytics: seo.analytics ?? "",
    language: seo.language ?? "",
    timeZone: seo.timeZone ?? "",
    pages: Object.fromEntries((seo.pages ?? []).map((page) => [page.path, { title: page.title ?? "", description: page.description ?? "" }])),
  };
}

const tidy = (text: string) => text.replace(/\s+/g, " ").trim();

/** Only what changed; an emptied field goes back to what the site's content says. */
export function siteDetailsChanges(seo: SavedSeo, form: SiteDetailsForm): SiteSet | null {
  const command: SiteSet = { kind: "site.set" };
  const title = form.title.trim();
  const description = tidy(form.description);
  if (title !== (seo.title ?? "")) command.title = title || null;
  if (description !== (seo.description ?? "")) command.description = description || null;
  if (form.icon !== undefined && !(form.icon === null && seo.icon === null)) command.icon = form.icon;
  if (form.image !== undefined && !(form.image === null && !seo.image)) command.image = form.image;
  if (form.analytics !== undefined) {
    const id = tagId(form.analytics);
    if (id !== (seo.analytics ?? "")) command.analytics = id ? { googleTagId: id } : null;
  }
  if (form.language !== undefined && form.language.trim() !== (seo.language ?? "")) command.language = form.language.trim() || null;
  if (form.timeZone !== undefined && form.timeZone.trim() !== (seo.timeZone ?? "")) command.timeZone = form.timeZone.trim() || null;
  const pages: NonNullable<SiteSet["pages"]> = {};
  for (const saved of seo.pages ?? []) {
    const edited = form.pages?.[saved.path];
    if (!edited) continue;
    const patch: { title?: string | null; description?: string | null } = {};
    const pageTitle = edited.title.trim();
    const pageDescription = tidy(edited.description);
    if (pageTitle !== (saved.title ?? "")) patch.title = pageTitle || null;
    if (pageDescription !== (saved.description ?? "")) patch.description = pageDescription || null;
    if (Object.keys(patch).length) pages[saved.path] = patch;
  }
  if (Object.keys(pages).length) command.pages = pages;
  return Object.keys(command).length > 1 ? command : null;
}

export function siteDetailsProblem(form: SiteDetailsForm): string | null {
  if (form.title.trim().length > MAX_TITLE) return `Keep the title under ${MAX_TITLE} characters.`;
  if (form.description.trim().length > MAX_DESCRIPTION) return `Keep the description under ${MAX_DESCRIPTION} characters.`;
  if (form.analytics !== undefined && tagId(form.analytics) && !GOOGLE_TAG_PATTERN.test(tagId(form.analytics))) return "Use a Google tag ID like G-ABC123XYZ.";
  for (const page of Object.values(form.pages ?? {})) {
    if (page.title.trim().length > MAX_TITLE) return `Keep page titles under ${MAX_TITLE} characters.`;
    if (page.description.trim().length > MAX_DESCRIPTION) return `Keep page descriptions under ${MAX_DESCRIPTION} characters.`;
  }
  return null;
}

/** The title and description a page shows when the owner leaves its own fields empty, following the site fields being edited. */
export function automaticPage(page: SiteSeo["pages"][number], siteTitle: string, siteDescription: string, fallback: string | null): PageDetailsForm {
  const title = page.label && page.label.trim().toLocaleLowerCase() !== siteTitle.trim().toLocaleLowerCase() ? `${page.label} · ${siteTitle}` : siteTitle;
  const description = page.path === "/" ? siteDescription || page.pageDescription || fallback : page.pageDescription || siteDescription || fallback;
  return { title, description: description ?? "" };
}

/** Cuts text where a search result would, with an ellipsis. */
export function shownAs(text: string, limit: number): string {
  const single = text.replace(/\s+/g, " ").trim();
  return single.length <= limit ? single : `${single.slice(0, limit - 1).trimEnd()}…`;
}
